import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import { AppBar, Box, IconButton, Stack, Theme, Toolbar, useMediaQuery } from "@mui/material";
import React, { ReactNode } from "react";
import { Defined, IDisable } from "sonobello.utilities.react";
import { SBPalette } from "sonobello.utilities.react.mui";

import SonoBelloLogo from "../../assets/Sono_Bello_horizontal_PNG.png";
import EnvironmentConfiguration from "../../types/EnvironmentConfiguration";
import { ViewKeyMap } from "../../types/Views";
import IPathObserver from "../../routing/types/IPathObserver";
import ResizingContainer from "./ResizingContainer";

export const ids = {
  homeButton: "header_homeButton",
  backButton: "header_backButton"
};

/** The App Bar component which is always visible at the top of the view. */
const Header: React.FC<IPathObserver> = ({ pathName }) => {
  const showMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const disableBackButton = Object.values(ViewKeyMap).some(({ uri }) => pathName.includes(uri));
  return (
    <AppBar
      color="default"
      sx={theme => ({
        zIndex: theme.zIndex.drawer + 1,
        backgroundColor: showMobile ? SBPalette.light.watermarkLightGray.main : SBPalette.light.white.main
      })}
    >
      <Toolbar sx={{ px: 0, display: "flex", alignItems: "center", overflow: "hidden", justifyContent: "center" }}>
        {showMobile ? (
          <MobileHeader disableBackButton={disableBackButton} />
        ) : (
          <DesktopHeader disableBackButton={disableBackButton} />
        )}
      </Toolbar>
    </AppBar>
  );
};

interface IHeaderProps {
  disableBackButton: boolean;
}

const DesktopHeader: React.FC<IHeaderProps> = ({ disableBackButton }) => (
  <ResizingContainer>
    <Box display="flex" justifyContent="space-between" alignItems="center" px={2}>
      <Stack direction="row" alignItems="center" spacing={3.5} sx={{ px: 2 }}>
        <BackButton disabled={disableBackButton} />
        <TitleLink>
          <Box alignItems="center" justifyContent="center">
            <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ width: "100%" }}>
              <img src={SonoBelloLogo} style={{ height: "45px" }} />
            </Stack>
          </Box>
        </TitleLink>
      </Stack>
      <HomeButton backgroundColor={SBPalette.light.paleGray.main} />
    </Box>
  </ResizingContainer>
);

const MobileHeader: React.FC<IHeaderProps> = ({ disableBackButton }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5} px={2} width="100%">
    <BackButton disabled={disableBackButton} />
    <TitleLink>
      <Stack justifyContent="center" alignItems="center" sx={{ width: "100%", pt: 0.35 }}>
        <img src={SonoBelloLogo} style={{ height: "35px" }} />
      </Stack>
    </TitleLink>
    <HomeButton backgroundColor={SBPalette.light.white.main} />
  </Stack>
);

const TitleLink: React.FC<{ children: ReactNode }> = ({ children }) => (
  <a
    href={EnvironmentConfiguration.escapeUrl}
    onClick={e => !confirm("Are you sure you want to leave the booker?") && e.preventDefault()}
    style={{ textDecoration: "none" }}
  >
    {children}
  </a>
);

const HomeButton: React.FC<{ backgroundColor: string }> = ({ backgroundColor }) => (
  <IconButton
    component="a"
    id={ids.homeButton}
    sx={{ p: 1, backgroundColor }}
    href={EnvironmentConfiguration.escapeUrl}
  >
    <HomeIcon />
  </IconButton>
);

const BackButton: React.FC<Defined<IDisable>> = ({ disabled }) => (
  <div>
    <IconButton
      id={ids.backButton}
      disabled={disabled}
      sx={{ p: 1, backgroundColor: SBPalette.light.white.main }}
      onClick={() => window.history.go(-1)}
    >
      <ArrowBackIcon />
    </IconButton>
  </div>
);

export default Header;
